/**
 * Follow-up state → chip label + a dsh status tone (reused RPA chip colors).
 * Staff board: labels mirror the state machine 1:1 (the last event that
 * happened), unlike the clinic-facing Recovery Loop page's friendly wording.
 * Shared by the SMS Activity table and its thread slideout.
 */
export type SmsStatusTone = 'errored' | 'running' | 'queued' | 'completed'

const STATUS_META: Record<string, { label: string; tone: SmsStatusTone }> = {
  awaiting_start: { label: 'Awaiting start', tone: 'queued' },
  // A Start-flow click stamped the check — the sweep sends it on its next in-window pass.
  queued: { label: 'Queued', tone: 'queued' },
  sms_sent: { label: 'SMS sent', tone: 'queued' },
  needs_clarification: { label: 'Needs clarification', tone: 'running' },
  awaiting_reply: { label: 'Awaiting reply', tone: 'queued' },
  extracted: { label: 'Extracted', tone: 'running' },
  cannot_extract: { label: 'Cannot extract', tone: 'errored' },
  sms_failed: { label: 'SMS failed', tone: 'errored' },
  no_phone: { label: 'No phone', tone: 'errored' },
  opted_out: { label: 'Opted out', tone: 'queued' },
  check_rerun: { label: 'Check re-run', tone: 'running' },
  // #304: the agent handed off and stopped. Without an entry here this fell
  // through to the raw string in the neutral `queued` tone, so a stopped case
  // read like one still waiting on the patient.
  // (Label mirrors the state, per this file's convention — the Recovery Loop
  // page says "Needs a person"; this board names the event.)
  escalated: { label: 'Escalated', tone: 'errored' },
  resolved: { label: 'Resolved', tone: 'completed' },
  rerun_failed: { label: 'Re-run failed', tone: 'errored' },
  extracted_same: { label: 'Extracted (same data)', tone: 'completed' },
  handled: { label: 'Handled', tone: 'completed' },
}

export function smsStatusMeta(s: string): { label: string; tone: SmsStatusTone } {
  return STATUS_META[s] ?? { label: s, tone: 'queued' }
}
